import { useMemo, useState } from 'react';
import { MegaphoneIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@components/ui';
import { useAuthStore } from '@features/auth/store/authStore';
import { normalizeRole } from '@lib/utils/roleNavigation';
import { BannerFormModal } from './BannerFormModal';
import { mensajeDeError, useBannerMutaciones, useBannersAdmin } from '../hooks/useBannerAdmin';
import { LAYOUTS_BANNER, ROLES_BANNER, estadoDe, type BannerAdmin } from '../types/announcement.types';

/**
 * Panel de banners — solo INTERNAL ADMIN (ni siquiera SUPER ADMIN).
 *
 * El candado se comprueba con el ROL, no con el flag `isSuperAdmin` de la
 * sesión: ese se calcula con `['ADMINISTRATOR','SUPER ADMIN','SUPERADMIN']`
 * (authService.ts) y no tiene nada que ver con `requirePolicy('InternalAdmin')`
 * del backend, que es exactamente `['INTERNAL ADMIN']` (auth.middleware.ts).
 * Un Super Admin real vería esta pantalla con el flag y se encontraría con un
 * 403 al guardar.
 *
 * Es defensa en profundidad, no el control: el backend rechaza igual.
 */

const ETIQUETA_ESTADO: Record<
  ReturnType<typeof estadoDe>,
  { texto: string; variant: 'success' | 'warning' | 'danger' | 'default' }
> = {
  vigente: { texto: 'Vigente', variant: 'success' },
  programado: { texto: 'Programado', variant: 'warning' },
  vencido: { texto: 'Vencido', variant: 'default' },
  apagado: { texto: 'Apagado', variant: 'default' },
};

function fechaCorta(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-PA', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Resumen legible del texto del banner, para la tabla. */
function resumen(b: BannerAdmin): string {
  if (b.titulo) return b.titulo;
  const plano = b.content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return plano.length > 70 ? `${plano.slice(0, 70)}…` : plano || '(sin contenido)';
}

export function BannerAdminPage() {
  const user = useAuthStore((s) => s.user);
  const puedeAdministrarBanners = normalizeRole(user?.role) === 'INTERNALADMIN';

  const { data, isLoading, error } = useBannersAdmin(puedeAdministrarBanners);
  const { actualizar, borrar } = useBannerMutaciones();

  const [modalAbierto, setModalAbierto] = useState(false);
  const [enEdicion, setEnEdicion] = useState<BannerAdmin | null>(null);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);

  // Los que se ven arriba en el portal, arriba también acá.
  const banners = useMemo(
    () => [...(data?.banners ?? [])].sort((a, b) => b.priority - a.priority),
    [data]
  );

  if (!puedeAdministrarBanners) {
    return (
      <div className="space-y-6 font-display">
        <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">Banners</h1>
        <Alert variant="warning">
          Esta sección es solo para Internal Admin.
        </Alert>
      </div>
    );
  }

  const abrirNuevo = () => {
    setEnEdicion(null);
    setModalAbierto(true);
  };

  const abrirEdicion = (b: BannerAdmin) => {
    setEnEdicion(b);
    setModalAbierto(true);
  };

  const alternarActivo = async (b: BannerAdmin) => {
    setErrorAccion(null);
    try {
      // PUT parcial: el backend parte del registro guardado, así que mandar
      // solo este campo no borra el resto.
      await actualizar.mutateAsync({ id: b.bannerAlertId, cambios: { isActive: !b.isActive } });
    } catch (e) {
      setErrorAccion(mensajeDeError(e));
    }
  };

  const eliminar = async (b: BannerAdmin) => {
    if (!window.confirm(`¿Borrar este banner?\n\n${resumen(b)}\n\nNo se puede deshacer.`)) return;
    setErrorAccion(null);
    try {
      await borrar.mutateAsync(b.bannerAlertId);
    } catch (e) {
      setErrorAccion(mensajeDeError(e));
    }
  };

  return (
    <div className="space-y-6 font-display" data-testid="banner-admin-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">Banners</h1>
          <p className="mt-2 text-secondary-600 dark:text-secondary-400">
            Noticias, promociones y avisos del portal. Los cambios salen sin desplegar.
          </p>
        </div>
        <Button onClick={abrirNuevo} data-testid="banner-nuevo">
          <MegaphoneIcon className="mr-2 h-5 w-5" />
          Nuevo banner
        </Button>
      </div>

      {errorAccion && <Alert variant="danger">{errorAccion}</Alert>}

      {error && (
        <Alert variant="danger">
          No pudimos leer el archivo de banners. {mensajeDeError(error)}
        </Alert>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : banners.length === 0 ? (
        <Card>
          <CardBody className="p-12 text-center">
            <MegaphoneIcon className="mx-auto mb-4 h-12 w-12 text-secondary-400" />
            <h3 className="mb-2 text-lg font-medium text-secondary-900 dark:text-white">
              Todavía no hay banners
            </h3>
            <p className="text-secondary-600 dark:text-secondary-400">
              Crea el primero y aparecerá en el dashboard de quien corresponda.
            </p>
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardBody className="p-0">
            <Table>
              <TableHeader>
                {/* Seis columnas, no ocho: con "Composición" y "Prioridad" sueltas
                    la tabla no entraba en 1440px y el toggle —que es la acción
                    principal de esta pantalla— quedaba fuera de la vista, detrás
                    de un scroll horizontal. Ahora van como subtítulo del contenido. */}
                <TableRow>
                  <TableHead>Contenido</TableHead>
                  <TableHead>Vigencia</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Activo</TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {banners.map((b) => {
                  const estado = ETIQUETA_ESTADO[estadoDe(b)];
                  const layout = LAYOUTS_BANNER.find((l) => l.value === b.layout)?.label ?? b.layout;
                  const roles = b.target.roles.length
                    ? b.target.roles
                        .map((r) => ROLES_BANNER.find((x) => x.value === r)?.label ?? r)
                        .join(', ')
                    : 'Todos';

                  return (
                    <TableRow key={b.bannerAlertId} data-testid={`banner-fila-${b.bannerAlertId}`}>
                      <TableCell>
                        <span className="block max-w-[420px] truncate font-medium text-secondary-900 dark:text-white">
                          {resumen(b)}
                        </span>
                        <span className="mt-0.5 block text-xs text-secondary-500 dark:text-secondary-400">
                          {layout} · prioridad {b.priority}
                        </span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {fechaCorta(b.startDate)} → {fechaCorta(b.endDate)}
                      </TableCell>
                      <TableCell className="max-w-[200px] text-sm">{roles}</TableCell>
                      <TableCell>
                        <Badge variant={estado.variant}>{estado.texto}</Badge>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={b.isActive}
                          onChange={() => alternarActivo(b)}
                          size="sm"
                          data-testid={`banner-toggle-${b.bannerAlertId}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => abrirEdicion(b)}
                            aria-label="Editar"
                            className="rounded p-1.5 text-secondary-500 transition-colors hover:bg-secondary-100 hover:text-secondary-900 dark:hover:bg-secondary-700 dark:hover:text-white"
                          >
                            <PencilSquareIcon className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => eliminar(b)}
                            aria-label="Borrar"
                            className="rounded p-1.5 text-secondary-500 transition-colors hover:bg-danger-50 hover:text-danger-600 dark:hover:bg-danger-950"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardBody>
        </Card>
      )}

      {data && (
        <p className="text-xs text-secondary-500 dark:text-secondary-400">
          Archivo versión {data.version} · última edición {fechaCorta(data.updatedAt)} por{' '}
          {data.updatedBy}
        </p>
      )}

      <BannerFormModal
        abierto={modalAbierto}
        banner={enEdicion}
        onCerrar={() => setModalAbierto(false)}
      />
    </div>
  );
}
